import { useLayoutEffect, useRef, useState } from 'react'

/**
 * Scales the handsets to whatever room the stage actually has.
 *
 * Measuring the container beats arithmetic on `window`, because the sidebar
 * width is set in CSS (and varies with the viewport) — this stays correct
 * without JS having to know that rule.
 */
export function usePhoneScale(phoneCount: number, frameWidth: number, frameHeight: number) {
  const ref = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(0.72)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return

    const GAP = 36
    /** Caption row above (20px), two-line caption below (34px), and both gaps. */
    const CHROME_H = 20 + 12 + 12 + 34

    const measure = () => {
      const { width, height } = el.getBoundingClientRect()
      if (!width || !height) return
      const usableW = width - GAP * (phoneCount - 1)
      const byWidth = usableW / (phoneCount * frameWidth)
      const byHeight = (height - CHROME_H) / frameHeight
      const next = Math.max(0.35, Math.min(1, byWidth, byHeight))
      setScale((prev) => (Math.abs(next - prev) > 0.004 ? next : prev))
    }

    measure()

    // Two signals on purpose. ResizeObserver is the accurate one: it catches
    // the stage changing for reasons the window knows nothing about, such as
    // the sidebar's clamp() crossing a breakpoint. The window listener is a
    // cheap hedge for viewports that resize without driving the observer —
    // emulated and embedded ones have been known to. Losing both would freeze
    // the handsets at their mount size, which is very visible on a projector.
    // `measure` early-outs when nothing moved, so the overlap costs a rect read.
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    window.addEventListener('resize', measure)

    return () => {
      ro.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [phoneCount, frameWidth, frameHeight])

  return { ref, scale }
}
